import { useEffect, useMemo } from 'react';

import { WidgetViewMode } from 'utilitys/reporting/widgetViewModeUtils';

import { UseBaseTable } from 'hooks/useBaseTable';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import {
    arrayBufferToString,
    generateTabs,
} from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { useUpdateIfReady } from 'components/Dashboards/Reporting/widgetInReport/hooks/RefreshWidgetPayload/useUpdateIfReady';
import { UseRelationshipCountChangesProvider } from 'components/widgets/RelationshipCountChanges/RelationshipCountChanges.provider';

interface Props {
    ui: UseRelationshipCountChangesProvider['ui'];
    api: UseRelationshipCountChangesProvider['api'];
    title: string;
    subTitle: string;
    table: UseBaseTable['table'];
    isSinglePage: boolean;
    setIsPayloadReady?: (newValue: boolean) => void;
    widgetViewMode?: WidgetViewMode;
}

export const useRelationshipCountChangesLogic = ({
    ui,
    api,
    title,
    subTitle,
    table,
    isSinglePage,
    setIsPayloadReady,
    widgetViewMode,
}: Props) => {
    const isPayloadReady = () => {
        const currentPayload = api.getRelationshipCountChangesTableData.payload;
        if (!currentPayload) return;
        if (
            currentPayload.compareVersionId === undefined ||
            currentPayload.latestVersionId === undefined ||
            currentPayload.contractId === undefined ||
            currentPayload.contractId === null ||
            currentPayload.projectId === undefined ||
            currentPayload.projectId === null
        ) {
            return;
        }
        setIsPayloadReady && setIsPayloadReady(true);
    };

    useUpdateIfReady({ isPayloadReadyCallback: isPayloadReady, widgetViewMode: widgetViewMode });

    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();

    const activeData = useMemo(() => {
        if (api.getRelationshipCountChangesTableData.instance.data?.periodItemsList) {
            const periodItemsList = api.getRelationshipCountChangesTableData.instance.data.periodItemsList;
            return (
                periodItemsList.find((item) => item.id === ui.state.activeTab)?.itemsList ||
                periodItemsList[0].itemsList
            );
        }
        return [];
    }, [api.getRelationshipCountChangesTableData, ui.state.activeTab]);

    const tableData = useMemo(() => {
        if (!activeData) return null;

        const statusArray = ui.state.statusFilter?.map((item) => item.value);
        const hasStatusFilter = statusArray && statusArray.length > 0;
        const hasCriticalPathFilter = ui.state.isCriticalPath;

        return activeData.filter((item) => {
            if (hasStatusFilter) {
                const itemStatus = item.latestVersion?.cardBoardViewResponse?.status?.name;
                if (!itemStatus || !statusArray.includes(itemStatus)) {
                    return false;
                }
            }

            if (hasCriticalPathFilter) {
                const isCriticalPath =
                    item.latestVersion?.cardBoardViewResponse?.cardIndicators?.isCriticalPath?.isIndicatorOn;
                if (isCriticalPath !== ui.state.isCriticalPath) {
                    return false;
                }
            }

            return true;
        });
    }, [activeData, ui.state.isCriticalPath, ui.state.statusFilter]);

    const handleAPIExcelExport = () => {
        api.exportCsvReport.instance.mutate(api.exportCsvReport.payload, {
            onSuccess: () => {
                handleSnackBar('File Successfully Downloaded', 'success');

                sendEventWithDimensions({
                    category: 'Export',
                    action: 'Excel',
                    label: `${title} ${subTitle}`,
                });
            },
            onError: (error) => {
                const message = arrayBufferToString(error?.response?.data);
                DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
            },
        });
    };

    const tabsArrData = generateTabs({ data: api.getRelationshipCountChangesTableData.instance.data });

    useEffect(() => {
        if (isSinglePage && tableData) {
            table.setPagination({ page: 1, size: tableData.length });
        }
    }, [isSinglePage, tableData?.length]);

    return {
        logic: {
            tableData,
            tabsArrData,
            handleAPIExcelExport,
        },
    };
};
